import React, { useCallback, useEffect, useRef, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import PropTypes from 'prop-types'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { makeStyles } from '@mui/styles'
import DEBOUNCE_TIMES from '@/modules/base/components/buttons/debounce-times'
import Direction from '@/enums/direction.enum'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'

export const VARIANTS = Object.freeze({
  white: 'white',
  grey: 'grey',
})

const useStyles = makeStyles((theme) => ({
  button: ({ variant }) => {
    return {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      outline: 'none',
      minHeight: '45px',
      minWidth: '45px',
      maxHeight: '45px',
      maxWidth: '45px',
      padding: '0',
      border: `1px solid ${theme.borderColor}`,
      borderRadius: '5px',
      background: variant === VARIANTS.white ? theme.backgroundColorLight : theme.backgroundColor,
      margin: '7.5px',
      '&:disabled svg': {
        fill: `${theme.lightGrey} !important`,
        '& [class*=st0], & [class*=st1], & [class]': {
          fill: `${theme.lightGrey} !important`,
        },
      },
      '&:hover:not(:disabled)': {
        backgroundColor: variant === VARIANTS.white ? theme.backgroundColor : theme.backgroundColorLight,
      },
      '&:hover .icon': {
        fill: theme.primary,
      },
      '&.has-hover:hover>span:first-child': {
        transition: '.2s',
        opacity: 0,
      },
      '&.has-hover:hover>span:last-child': {
        transition: '.2s',
        opacity: 1,
      },
    }
  },
  icon: ({ iconColor }) => ({
    fill: iconColor || theme.DocumentControlButton.iconColor,
  }),
  iconActive: {
    transition: '.2s',
    fill: theme.primary,
    opacity: 0,
    position: 'absolute',
  },
  tooltip: {
    fontSize: 11,
  },
}))

const DocumentControlButton = (props) => {
  const {
    id = null,
    actionId,
    iconColor,
    icon,
    iconActive,
    onClick,
    customClass,
    width = '15px',
    height = '15px',
    title,
    disabled = false,
    variant = VARIANTS.white,
    debounceTime = DEBOUNCE_TIMES.DEFAULT,
    dataQsId,
    tooltip = '',
  } = props
  const classes = useStyles({ variant, iconColor })
  const rootClassNames = [customClass || '', classes.button]
  const [clickDebounce, setClickDebounce] = useState(false)
  const onClickRef = useRef()
  onClickRef.current = onClick
  const clickDebounceTimeOutRef = useRef()

  useEffect(() => {
    return () => {
      clearTimeout(clickDebounceTimeOutRef.current)
    }
  }, [])

  const onClickHandle = useCallback((e) => {
    if (!clickDebounce) {
      if (debounceTime > 0) {
        setClickDebounce(true)
      }
      onClickRef.current && onClickRef.current(e, actionId)
      if (debounceTime > 0) {
        clickDebounceTimeOutRef.current = setTimeout(() => {
          setClickDebounce(false)
        }, debounceTime)
      }
    }
  }, [debounceTime, clickDebounce, actionId])

  if (iconActive) {
    rootClassNames.push('has-hover')
  }

  let button = (
    <button
      data-qs-id={dataQsId}
      id={id}
      className={rootClassNames.join(' ')}
      type='button'
      disabled={disabled || clickDebounce}
      onClick={onClickHandle}>
      <SVGIcon width={width} height={height} svg={icon} customClass={classes.icon} fill={iconColor} />
      {iconActive && <SVGIcon width={width} height={height} svg={iconActive} customClass={classes.iconActive} />}
    </button>
  )

  if (title && !disabled) {
    button = (
      <Tooltip title={title} aria-label={title} PopperProps={{ container: document.fullscreenElement ?? document.body }} classes={{ tooltip: classes.tooltip }}>
        <span>{button}</span>
      </Tooltip>
    )
  }

  if (tooltip) {
    button = (
      <RichTooltip
        content={(<div className={classes.tooltip}>{tooltip}</div>)}
        placement={Direction.BOTTOM}
        width={300}>
        <span>
          {button}
        </span>
      </RichTooltip>
    )
  }

  return button
}

DocumentControlButton.propTypes = {
  id: PropTypes.string,
  actionId: PropTypes.string,
  iconColor: PropTypes.string,
  icon: PropTypes.elementType,
  iconActive: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  customClass: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  title: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf([...Object.values(VARIANTS)]),
  debounceTime: PropTypes.number,
  tooltip: PropTypes.string,
}

export default DocumentControlButton
